/**
 * @fileoverview Role-Based Access Control (RBAC) Hooks
 *
 * Provides hooks for checking user permissions and enforcing access control
 * in the UI based on user roles and tenant membership.
 *
 * Permission Hierarchy:
 * - owner: Full access to all features
 * - manager: Most features except billing and tenant settings
 * - dispatcher: Job scheduling, technician assignment, customer management
 * - technician: Own jobs, survey completion, limited customer view
 *
 * @module hooks/use-permissions
 */

import { useAuth } from "@/contexts/AuthContext";
import { type TenantUserRole } from "@/lib/auth";

// ============================================================================
// Types
// ============================================================================

export type Permission =
  // Job permissions
  | "jobs:view"
  | "jobs:create"
  | "jobs:update"
  | "jobs:delete"
  | "jobs:assign"
  | "jobs:schedule"
  // Customer permissions
  | "customers:view"
  | "customers:create"
  | "customers:update"
  | "customers:delete"
  // Technician management
  | "technicians:view"
  | "technicians:create"
  | "technicians:update"
  | "technicians:delete"
  // Survey/Equipment
  | "surveys:view"
  | "surveys:create"
  | "surveys:update"
  // Campaigns/Marketing
  | "campaigns:view"
  | "campaigns:create"
  | "campaigns:update"
  | "campaigns:delete"
  // Analytics
  | "analytics:view"
  | "analytics:export"
  // Settings
  | "settings:view"
  | "settings:update"
  | "billing:view"
  | "billing:update"
  | "integrations:view"
  | "integrations:update"
  // Team management
  | "team:view"
  | "team:invite"
  | "team:update"
  | "team:remove";

// ============================================================================
// Permission Mappings
// ============================================================================

/**
 * Define which permissions each role has
 */
const ROLE_PERMISSIONS: Record<TenantUserRole, Permission[]> = {
  owner: [
    // Full access to everything
    "jobs:view", "jobs:create", "jobs:update", "jobs:delete", "jobs:assign", "jobs:schedule",
    "customers:view", "customers:create", "customers:update", "customers:delete",
    "technicians:view", "technicians:create", "technicians:update", "technicians:delete",
    "surveys:view", "surveys:create", "surveys:update",
    "campaigns:view", "campaigns:create", "campaigns:update", "campaigns:delete",
    "analytics:view", "analytics:export",
    "settings:view", "settings:update",
    "billing:view", "billing:update",
    "integrations:view", "integrations:update",
    "team:view", "team:invite", "team:update", "team:remove",
  ],

  manager: [
    // Most features except billing and critical settings
    "jobs:view", "jobs:create", "jobs:update", "jobs:delete", "jobs:assign", "jobs:schedule",
    "customers:view", "customers:create", "customers:update", "customers:delete",
    "technicians:view", "technicians:create", "technicians:update", "technicians:delete",
    "surveys:view", "surveys:create", "surveys:update",
    "campaigns:view", "campaigns:create", "campaigns:update", "campaigns:delete",
    "analytics:view", "analytics:export",
    "settings:view",
    "integrations:view", "integrations:update",
    "team:view", "team:invite",
  ],

  dispatcher: [
    // Job and schedule management, customer interaction
    "jobs:view", "jobs:create", "jobs:update", "jobs:assign", "jobs:schedule",
    "customers:view", "customers:create", "customers:update",
    "technicians:view",
    "surveys:view",
    "analytics:view",
  ],

  technician: [
    // Limited to own work
    "jobs:view",
    "jobs:update", // Only own jobs (status updates)
    "customers:view", // Limited view
    "surveys:view", "surveys:create", "surveys:update",
  ],
};

// ============================================================================
// Hook: usePermissions
// ============================================================================

/**
 * Hook for checking user permissions based on their role.
 *
 * @example
 * ```tsx
 * function JobActions({ job }) {
 *   const { can, canAny, canAll } = usePermissions();
 *
 *   return (
 *     <div>
 *       {can("jobs:update") && <EditButton />}
 *       {can("jobs:delete") && <DeleteButton />}
 *       {canAny(["jobs:assign", "jobs:schedule"]) && <AssignButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions() {
  const { currentRole, isMaster } = useAuth();

  /**
   * Check if user has a specific permission
   */
  const can = (permission: Permission): boolean => {
    // Master accounts have all permissions
    if (isMaster) return true;

    // No role = no permissions
    if (!currentRole) return false;

    return ROLE_PERMISSIONS[currentRole]?.includes(permission) ?? false;
  };

  /**
   * Check if user has ANY of the specified permissions
   */
  const canAny = (permissions: Permission[]): boolean => {
    return permissions.some(p => can(p));
  };

  /**
   * Check if user has ALL of the specified permissions
   */
  const canAll = (permissions: Permission[]): boolean => {
    return permissions.every(p => can(p));
  };

  /**
   * Check if user has a specific role or higher
   */
  const hasRole = (role: TenantUserRole): boolean => {
    if (isMaster) return true;
    if (!currentRole) return false;

    const roleHierarchy: TenantUserRole[] = ["technician", "dispatcher", "manager", "owner"];
    const currentIndex = roleHierarchy.indexOf(currentRole);
    const requiredIndex = roleHierarchy.indexOf(role);

    return currentIndex >= requiredIndex;
  };

  /**
   * Check if user is exactly a specific role
   */
  const isRole = (role: TenantUserRole): boolean => {
    if (isMaster) return role === "owner";
    return currentRole === role;
  };

  /**
   * Get all permissions for the current user
   */
  const getAllPermissions = (): Permission[] => {
    if (isMaster) {
      return ROLE_PERMISSIONS.owner;
    }
    if (!currentRole) return [];
    return ROLE_PERMISSIONS[currentRole] ?? [];
  };

  return {
    can,
    canAny,
    canAll,
    hasRole,
    isRole,
    getAllPermissions,
    currentRole,
    isMaster,
  };
}

// ============================================================================
// Hook: useRoleCheck
// ============================================================================

/**
 * Simple hook for role-based checks.
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const { isOwner, isManager, isDispatcher, isTechnician } = useRoleCheck();
 *
 *   if (!isOwner && !isManager) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <Settings />;
 * }
 * ```
 */
export function useRoleCheck() {
  const { currentRole, isMaster } = useAuth();

  return {
    isOwner: isMaster || currentRole === "owner",
    isManager: isMaster || currentRole === "manager" || currentRole === "owner",
    isDispatcher:
      isMaster ||
      currentRole === "dispatcher" ||
      currentRole === "manager" ||
      currentRole === "owner",
    isTechnician: currentRole === "technician",
    currentRole,
    isMaster,
  };
}
